<script setup lang="ts">
import { onMounted, computed, nextTick, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { screenModule } from '@/store/screen/module'
import { useRouter } from 'vue-router'

const router = useRouter()

const {
    ModalContainer,
    Button: PocketButton,
    Icon,
} = UIKit

const emit = defineEmits<{
    close: []
    tryLevelUp: []
}>()

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const dialogRef = ref<HTMLElement | null>(null)

onMounted(async () => {
    await nextTick()
    dialogRef.value?.focus()
})

const markModalSeen = async (callToAction: 'Explore on my own' | 'Try a Level Up Quiz') => {
    analyticsModule.actions.amplitudeTrack('Level Up Trial CTA Seen')
    await userModule.actions.markUserHasSeenLevelUpTrialSuggestion()
    analyticsModule.actions.amplitudeTrack('Level Up Trial CTA Clicked', {
        callToAction,
    })
}

const handleClose = async () => {
    await markModalSeen('Explore on my own')
    emit('close')
}

const handleTryLevelUp = async () => {
    await markModalSeen('Try a Level Up Quiz')
    await router.push({
        name: 'level-up-intro',
        query: {
            step: 'selectSubject',
        },
    })
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="tryLevelUpModal"
            :show-close-button="breakpoint !== 'black-bear'"
            @close="handleClose"
        >
            <div
                class="try-level-up-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="try-level-up-modal-title"
                aria-describedby="try-level-up-modal-description"
            >
                <div class="try-level-up-modal__message">
                    <div
                        id="try-level-up-modal-title"
                        ref="dialogRef"
                        class="try-level-up-modal__welcome"
                        role="heading"
                        aria-level="2"
                        aria-describedby="try-level-up-modal-description"
                        tabindex="-1"
                    >
                        Welcome to Pocket Prep!
                    </div>
                    <div
                        id="try-level-up-modal-description"
                        class="try-level-up-modal__description"
                    >
                        Not sure where to start?
                        How about a free preview of our most-loved premium quiz mode: Level Up.
                    </div>
                </div>
                <div class="try-level-up-modal__actions">
                    <PocketButton
                        class="try-level-up-modal__no-thanks"
                        type="secondary"
                        @click="handleClose"
                    >
                        Explore on my own
                    </PocketButton>
                    <PocketButton
                        class="try-level-up-modal__try-button"
                        @click="handleTryLevelUp"
                    >
                        Try a Level Up Quiz<Icon class="try-level-up-modal__try-button-arrow" type="arrow" />
                    </PocketButton>
                </div>
                <img
                    class="try-level-up-modal__brain-launching-image"
                    src="@/assets/brain/brain-launching.png"
                    alt=""
                >
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.try-level-up-modal {
    width: 1010px;
    background-color: $floral-white;
    height: calc(80vh - 56px);
    min-height: 640px;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;

    @include breakpoint(brown-bear) {
        width: 740px;
        height: calc(100vh - 140px) !important;
    }

    @include breakpoint(black-bear) {
        top: 0;
        max-height: none;
        height: 100% !important;
        width: 100%;
        min-height: 660px;
        border-radius: 0;
    }

    &__message {
        display: flex;
        width: 360px;
        flex-direction: column;
        align-items: center;
        text-align: center;
        margin-top: 78px;
    }

    &__welcome {
        color: $brand-black;
        font-size: 26px;
        font-weight: 700;
        line-height: normal;
        margin-bottom: 12px;

        &:focus {
            outline: none;
        }
    }

    &__description {
        color: $brand-black;
        font-size: 15px;
        line-height: 20px;
        margin-bottom: 48px;

        @include breakpoint(black-bear) {
            width: 282px;
            text-align: center;
        }
    }

    &__brain-launching-image {
        display: flex;
        position: relative;
        flex-direction: column;
        align-items: center;
        width: 768px;
        height: 386px;
        margin-top: 44px;
        overflow: hidden;

        @include breakpoint(brown-bear) {
            padding-right: 12px;
        }

        @include breakpoint(black-bear) {
            margin-top: 12px;
            padding-right: 48px;
        }
    }

    &__actions {
        display: flex;
        gap: 8px;

        @include breakpoint(black-bear) {
            flex-direction: column-reverse;
            gap: 24px;
        }
    }

    &__try-button-arrow{
        margin-left: 8px;
    }
}
</style>
